import { BaseQueryFn, EndpointBuilder, QueryReturnValue } from '@reduxjs/toolkit/query';
import { ResponseCompanyDto } from '../types/dtos';
import { APP_ENDPOINTS } from 'app/endpoints';
import { handleDuplicates, removeUndefined } from 'utils/array-helpers';
import { PaginatedResponse } from 'types/dtos';
import { constructQueryParams } from 'utils/query-params-builder';
import { BaseQueryParams } from 'types/base-query-params';
import { Document } from 'services/documents/types/dtos';
import { formatDate } from 'utils/date-helpers';
import { ResponseCompanyOwnerDto } from 'features/company-owner/types/dtos';
import { ResponseCityDto } from 'features/city/types/dtos';

export type CompaniesWithJoinQueryParams = BaseQueryParams & {
    name?: string;
    companyOwnerId?: string;
    createdAt?: string;
};

export default (build: EndpointBuilder<BaseQueryFn, string, string>) =>
    build.query<PaginatedResponse<ResponseCompanyDto>, CompaniesWithJoinQueryParams>({
        async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
            const fetchResponse = async (ENDPOINT: string, idsArray: string[]): Promise<QueryReturnValue> => {
                return await fetchWithBQ(
                    constructQueryParams(ENDPOINT, {
                        page: 1,
                        size: idsArray.length,
                        ids: idsArray.join(','),
                    }),
                );
            };

            const checkError = (response: QueryReturnValue) => {
                if (response.error) {
                    return { error: response.error };
                }
            };

            const companiesResponse = await fetchWithBQ(constructQueryParams(APP_ENDPOINTS.COMPANIES, _arg));
            const companiesErorr = checkError(companiesResponse);
            if (companiesErorr) return companiesErorr;

            const companies = companiesResponse.data as PaginatedResponse<ResponseCompanyDto>;

            const companyOwnerIds = removeUndefined(
                handleDuplicates(
                    companies.content.flatMap((company) => (company.companyOwnerId ? [company.companyOwnerId] : [])),
                ),
            );
            const documentIds = handleDuplicates(
                removeUndefined(companies.content.map((company) => company.documentId)),
            );

            const companyOwnersResponse = await fetchResponse(APP_ENDPOINTS.COMPANY_OWNERS, companyOwnerIds);
            const companyOwnersErorr = checkError(companyOwnersResponse);
            if (companyOwnersErorr) return companyOwnersErorr;

            const citiesIds = removeUndefined(handleDuplicates(companies.content.map((company) => company.cityId)));
            const citiesResponse = await fetchResponse(APP_ENDPOINTS.CITIES, citiesIds);
            const citiesError = checkError(citiesResponse);
            if (citiesError) return citiesError;
            const cities = citiesResponse.data as PaginatedResponse<ResponseCityDto>;

            let documents: PaginatedResponse<Document>;
            if (documentIds.length > 0) {
                const documentResponse = await fetchResponse(APP_ENDPOINTS.DOCUMENTS, documentIds);
                const documentError = checkError(documentResponse);
                if (documentError) return documentError;
                documents = documentResponse.data as PaginatedResponse<Document>;
            }

            const companyOwners = companyOwnersResponse.data as PaginatedResponse<ResponseCompanyOwnerDto>;

            companies.content = companies.content.map((company) => {
                const companyOwnerName = companyOwners.content.find((companyOwner) => {
                    return companyOwner.id === company.companyOwnerId;
                })?.fullName;

                const filePath = documents?.content.find((document) => {
                    return document.id === company.documentId;
                })?.filePaths;

                const cityName = cities.content.find((city) => {
                    return city.id === company.cityId;
                })?.name;

                const createdAt = formatDate(company.createdAt);

                const imageUrl = filePath && JSON.parse(filePath).small;

                return {
                    ...company,
                    companyOwnerName,
                    cityName,
                    createdAt,
                    imageUrl,
                };
            });

            return {
                data: companies,
            };
        },
        providesTags: [{ type: 'companies-with-join', id: 'GET-JOIN' }],
    });
